/**
 * @fileoverview Types for ShipHero integration
 *
 * Including:
 * - Types corresponding to rjsf schemas for ShipHero integration action steps inputs and results
 * - Types for request body sent to ShipHero API
 * - Types for ShipHero API response data
 *
 * Organized by entities:
 * - Order
 * - Purchase Order
 */

import * as yup from 'yup'

// SHARED -----------------------------------------------------------------------------------------

/**
 * ShipHero Address Schema
 *
 * Also used as action step input schema for address inputs
 */
export const shipHeroAddressSchema = yup.object({
  first_name: yup.string().notRequired(),
  last_name: yup.string().notRequired(),
  company: yup.string().notRequired(),
  address1: yup.string().notRequired(),
  address2: yup.string().notRequired(),
  city: yup.string().notRequired(),
  state: yup.string().notRequired(),
  state_code: yup.string().notRequired(),
  zip: yup.string().notRequired(),
  country: yup.string().notRequired(),
  country_code: yup.string().notRequired(),
  email: yup.string().notRequired(),
  phone: yup.string().notRequired(),
})

// ORDER -----------------------------------------------------------------------------------------

/**
 * ShipHero Shipping Line Schema
 */
export const shipHeroShippingLinesSchema = yup.object({
  title: yup.string().notRequired(),
  price: yup.string().notRequired(),
  carrier: yup.string().notRequired(),
  method: yup.string().notRequired(),
})

/**
 * ShipHero Line Item Schema
 *
 * Also used as action step input schema for line item inputs
 */
export const lineItemSchema = yup.object({
  sku: yup.string().notRequired(),
  partner_line_item_id: yup.string().notRequired(),
  quantity: yup.number().notRequired(),
  price: yup.string().notRequired(),
  product_name: yup.string().notRequired(),
  fulfillment_status: yup.string().notRequired(),
  quantity_pending_fulfillment: yup.number().notRequired(),
  warehouse_id: yup.string().notRequired(),
})

/** ShipHero Order Schema */
export const shipHeroOrderSchema = yup.object({
  order_number: yup.string().notRequired(),
  shop_name: yup.string().notRequired(),
  fulfillment_status: yup.string().notRequired(),
  order_date: yup.string().notRequired(),
  total_tax: yup.string().notRequired(),
  subtotal: yup.string().notRequired(),
  total_discounts: yup.string().notRequired(),
  total_price: yup.string().notRequired(),
  shipping_lines: shipHeroShippingLinesSchema.notRequired(),
  shipping_address: shipHeroAddressSchema.notRequired(),
  billing_address: shipHeroAddressSchema.notRequired(),
  line_items: yup.array().of(lineItemSchema).notRequired(),
  tags: yup.array().of(yup.string()).notRequired(),
  packing_note: yup.string().notRequired(),
  gift_note: yup.string().notRequired(),
  required_ship_date: yup.string().notRequired(),
})

export type ShipHeroOrder = yup.InferType<typeof shipHeroOrderSchema>

/** Which ShipHero mutation to run for an order UPDATE write step. */
export const ShipHeroOrderUpdateOperation = {
  /** order_update — address, shipping lines, notes (not fulfillment_status). */
  ORDER: 'order',
  /** order_update_line_items — partial line fulfillment updates. */
  LINE_ITEMS: 'line_items',
  /** order_update_fulfillment_status — whole-order fulfillment status. */
  FULFILLMENT_STATUS: 'fulfillment_status',
} as const

export type ShipHeroOrderUpdateOperationType =
  (typeof ShipHeroOrderUpdateOperation)[keyof typeof ShipHeroOrderUpdateOperation]

const isNonEmptyString = (value: string | null | undefined): boolean => {
  return value != null && value.trim() !== ''
}

const isShippingLinesProvided = (
  shippingLines:
    | {
        title?: string | null
        price?: string | null
        carrier?: string | null
        method?: string | null
      }
    | null
    | undefined,
): boolean => {
  if (shippingLines == null) {
    return false
  }
  return (
    shippingLines.title != null ||
    shippingLines.price != null ||
    shippingLines.carrier != null ||
    shippingLines.method != null
  )
}

/**
 * Line item patch for order_update_line_items.
 *
 * @see https://developer.shiphero.com/schema/mutations/order-update-line-items.html
 */
export const shipHeroUpdateLineItemSchema = yup.object({
  id: yup.string().required(),
  partner_line_item_id: yup.string().notRequired(),
  quantity: yup.number().integer().notRequired(),
  price: yup.string().notRequired(),
  product_name: yup.string().notRequired(),
  option_title: yup.string().notRequired(),
  fulfillment_status: yup.string().notRequired(),
  quantity_pending_fulfillment: yup.number().integer().notRequired(),
  warehouse_id: yup.string().notRequired(),
  barcode: yup.string().notRequired(),
})

/**
 * Write-step input for ShipHero order UPDATE (all three mutation types).
 *
 * @see https://developer.shiphero.com/examples/#orderupdate
 */
export const shipHeroUpdateOrderActionStepInputSchema = yup
  .object({
    order_id: yup.string().required(),
    customer_account_id: yup.string().notRequired(),
    /**
     * When false (default), fail before calling ShipHero if the order already has shipping
     * labels — updates are rejected after label generation.
     */
    skip_label_check: yup.boolean().default(false),
    update_operation: yup.string().oneOf(Object.values(ShipHeroOrderUpdateOperation)).required(),
    shipping_address: shipHeroAddressSchema.notRequired(),
    billing_address: shipHeroAddressSchema.notRequired(),
    shipping_lines: shipHeroShippingLinesSchema.notRequired(),
    packing_note: yup.string().notRequired(),
    gift_note: yup.string().notRequired(),
    order_number: yup.string().notRequired(),
    partner_order_id: yup.string().notRequired(),
    order_date: yup.string().notRequired(),
    total_tax: yup.string().notRequired(),
    total_discounts: yup.string().notRequired(),
    required_ship_date: yup.string().notRequired(),
    profile: yup.string().notRequired(),
    priority_flag: yup.boolean().notRequired(),
    tags: yup.array().of(yup.string()).notRequired(),
    ignore_address_validation_errors: yup.boolean().notRequired(),
    skip_address_validation: yup.boolean().notRequired(),
    line_items: yup.array().of(shipHeroUpdateLineItemSchema).notRequired(),
    fulfillment_status: yup.string().notRequired(),
    reason: yup.string().notRequired(),
    remove_inventory: yup.boolean().notRequired(),
    void_on_platform: yup.boolean().notRequired(),
  })
  .test('shiphero-order-update-operation', 'Invalid ShipHero order update input', function (value) {
    if (value == null) {
      return true
    }

    if (value.update_operation === ShipHeroOrderUpdateOperation.LINE_ITEMS) {
      if (!value.line_items || value.line_items.length === 0) {
        return this.createError({
          path: 'line_items',
          message: 'line_items is required for line_items update operation',
        })
      }
      return true
    }

    if (value.update_operation === ShipHeroOrderUpdateOperation.FULFILLMENT_STATUS) {
      if (!isNonEmptyString(value.fulfillment_status)) {
        return this.createError({
          path: 'fulfillment_status',
          message: 'fulfillment_status is required for fulfillment_status update operation',
        })
      }
      return true
    }

    if (isShippingLinesProvided(value.shipping_lines)) {
      if (!isNonEmptyString(value.shipping_lines?.title)) {
        return this.createError({
          path: 'shipping_lines.title',
          message: 'shipping_lines.title is required when shipping_lines is provided',
        })
      }
      if (!isNonEmptyString(value.shipping_lines?.price)) {
        return this.createError({
          path: 'shipping_lines.price',
          message: 'shipping_lines.price is required when shipping_lines is provided',
        })
      }
    }

    const hasOrderField =
      value.shipping_address != null ||
      value.billing_address != null ||
      isShippingLinesProvided(value.shipping_lines) ||
      isNonEmptyString(value.packing_note) ||
      isNonEmptyString(value.gift_note) ||
      isNonEmptyString(value.order_number) ||
      isNonEmptyString(value.partner_order_id) ||
      isNonEmptyString(value.order_date) ||
      isNonEmptyString(value.total_tax) ||
      isNonEmptyString(value.total_discounts) ||
      isNonEmptyString(value.required_ship_date) ||
      isNonEmptyString(value.profile) ||
      value.priority_flag != null ||
      value.ignore_address_validation_errors != null ||
      value.skip_address_validation != null ||
      (value.tags != null && value.tags.length > 0)

    if (!hasOrderField) {
      return this.createError({
        path: 'update_operation',
        message:
          'order update requires at least one field (e.g. shipping_address, shipping_lines, ignore_address_validation_errors)',
      })
    }

    return true
  })

export type ShipHeroUpdateOrderActionStepInput = yup.InferType<
  typeof shipHeroUpdateOrderActionStepInputSchema
>

export const shipHeroOrderUpdateMutationResponseSchema = yup.object({
  data: yup
    .object({
      order_update: yup
        .object({
          request_id: yup.string().notRequired(),
          order: yup.object({ id: yup.string().required() }).notRequired(),
        })
        .notRequired(),
      order_update_line_items: yup
        .object({
          request_id: yup.string().notRequired(),
          order: yup.object({ id: yup.string().required() }).notRequired(),
        })
        .notRequired(),
      order_update_fulfillment_status: yup
        .object({
          request_id: yup.string().notRequired(),
          order: yup.object({ id: yup.string().required() }).notRequired(),
        })
        .notRequired(),
    })
    .required(),
})

/**
 * Write-step input for ShipHero order CANCEL via order_cancel.
 *
 * order_id must be the ShipHero GraphQL global ID (e.g. T3JkZXI6...), not partner_order_id.
 *
 * @see https://developer.shiphero.com/schema/mutations/order-cancel.html
 */
export const shipHeroCancelOrderActionStepInputSchema = yup.object({
  order_id: yup.string().required(),
  customer_account_id: yup.string().notRequired(),
  reason: yup.string().notRequired(),
  void_on_platform: yup.boolean().notRequired(),
  /** Cancel even if the order has valid labels and completed shipments */
  force: yup.boolean().notRequired(),
})

export type ShipHeroCancelOrderActionStepInput = yup.InferType<
  typeof shipHeroCancelOrderActionStepInputSchema
>

export const shipHeroOrderCancelMutationResponseSchema = yup.object({
  data: yup.object({
    order_cancel: yup.object({
      request_id: yup.string().notRequired(),
      complexity: yup.number().notRequired(),
      order: yup.object({ id: yup.string().required() }).notRequired(),
    }),
  }),
})

/**
 * Write-step input for ShipHero inventory transfer via inventory_transfer.
 *
 * Atomic bin-to-bin / LPN transfer within a warehouse. DOSS must model in-transit
 * inventory separately when moving between warehouses (e.g. ShipHero to FBA).
 *
 * @see https://developer.shiphero.com/schema/mutations/inventory-transfer.html
 */
export const shipHeroInventoryTransferActionStepInputSchema = yup
  .object({
    sku: yup.string().required(),
    warehouse_id: yup.string().required(),
    quantity: yup.number().integer().min(1).required(),
    customer_account_id: yup.string().notRequired(),
    location_from_id: yup.string().notRequired(),
    location_to_id: yup.string().notRequired(),
    reason: yup.string().notRequired(),
    lpn_from_id: yup.string().notRequired(),
    lpn_to_id: yup.string().notRequired(),
  })
  .test(
    'shiphero-inventory-transfer-endpoints',
    'inventory_transfer requires location_from_id and location_to_id, or lpn_from_id and lpn_to_id',
    function (value) {
      if (value == null) {
        return true
      }
      if (
        !isNonEmptyString(value.sku) ||
        !isNonEmptyString(value.warehouse_id) ||
        value.quantity == null
      ) {
        return true
      }
      const hasLocationPair =
        isNonEmptyString(value.location_from_id) && isNonEmptyString(value.location_to_id)
      const hasLpnPair = isNonEmptyString(value.lpn_from_id) && isNonEmptyString(value.lpn_to_id)
      if (hasLocationPair || hasLpnPair) {
        return true
      }
      return this.createError({
        path: 'location_from_id',
        message:
          'Provide location_from_id and location_to_id, or lpn_from_id and lpn_to_id for the transfer',
      })
    },
  )

export type ShipHeroInventoryTransferActionStepInput = yup.InferType<
  typeof shipHeroInventoryTransferActionStepInputSchema
>

export const shipHeroInventoryTransferMutationResponseSchema = yup.object({
  data: yup.object({
    inventory_transfer: yup.object({
      request_id: yup.string().notRequired(),
      complexity: yup.number().notRequired(),
      ok: yup.boolean().notRequired(),
    }),
  }),
})

// PURCHASE ORDER -----------------------------------------------------------------------------------------

// Line item schema for purchase orders
export const shipHeroPurchaseOrderLineItemSchema = yup.object({
  sku: yup.string().notRequired(),
  quantity: yup.number().notRequired(),
  expected_weight_in_lbs: yup.string().notRequired(),
  vendor_id: yup.string().notRequired(),
  quantity_received: yup.number().notRequired(),
  quantity_rejected: yup.number().notRequired(),
  price: yup.string().notRequired(),
  product_name: yup.string().notRequired(),
  fulfillment_status: yup.string().notRequired(),
  sell_ahead: yup.number().notRequired(),
})

// Purchase order data schema
export const shipHeroPurchaseOrderDataSchema = yup.object({
  po_date: yup.string().notRequired(),
  po_number: yup.string().notRequired(),
  subtotal: yup.string().notRequired(),
  tax: yup.string().notRequired(),
  shipping_price: yup.string().notRequired(),
  total_price: yup.string().notRequired(),
  warehouse_id: yup.string().notRequired(),
  line_items: yup.array().of(shipHeroPurchaseOrderLineItemSchema).notRequired(),
  fulfillment_status: yup.string().notRequired(),
  discount: yup.string().notRequired(),
  vendor_id: yup.string().notRequired(),
})

export type ShipHeroPurchaseOrder = yup.InferType<typeof shipHeroPurchaseOrderDataSchema>

/**
 * Write-step input line item for ShipHero purchase order UPDATE via purchase_order_update.
 *
 * Matches ShipHero's UpdatePurchaseOrderLineItemInput: sku identifies the line, the rest are
 * the editable fields. Quantity fields are integers; price/note are strings.
 *
 * @see https://developer.shiphero.com/schema/mutations/purchase-order-update.html
 */
export const shipHeroUpdatePurchaseOrderLineItemSchema = yup.object({
  sku: yup.string().required(),
  quantity: yup.number().integer().notRequired(),
  quantity_received: yup.number().integer().notRequired(),
  quantity_rejected: yup.number().integer().notRequired(),
  sell_ahead: yup.number().integer().notRequired(),
  price: yup.string().notRequired(),
  note: yup.string().notRequired(),
})

/**
 * Write-step input for ShipHero purchase order UPDATE via purchase_order_update.
 *
 * po_id must be the ShipHero GraphQL global ID of the purchase order (the `id` returned by
 * purchase_order_create), not the legacy numeric id or po_number. All other fields are optional
 * partial edits; only provided fields are sent to ShipHero.
 *
 * @see https://developer.shiphero.com/schema/mutations/purchase-order-update.html
 */
export const shipHeroUpdatePurchaseOrderActionStepInputSchema = yup.object({
  po_id: yup.string().required(),
  customer_account_id: yup.string().notRequired(),
  po_date: yup.string().notRequired(),
  /** Clear the existing po_date instead of setting one */
  clear_po_date: yup.boolean().notRequired(),
  tax: yup.string().notRequired(),
  discount: yup.string().notRequired(),
  shipping_price: yup.string().notRequired(),
  shipping_method: yup.string().notRequired(),
  shipping_carrier: yup.string().notRequired(),
  shipping_name: yup.string().notRequired(),
  tracking_number: yup.string().notRequired(),
  packing_note: yup.string().notRequired(),
  po_note: yup.string().notRequired(),
  description: yup.string().notRequired(),
  partner_order_number: yup.string().notRequired(),
  payment_method: yup.string().notRequired(),
  payment_note: yup.string().notRequired(),
  line_items: yup.array().of(shipHeroUpdatePurchaseOrderLineItemSchema).notRequired(),
})

export type ShipHeroUpdatePurchaseOrderActionStepInput = yup.InferType<
  typeof shipHeroUpdatePurchaseOrderActionStepInputSchema
>

export const shipHeroPurchaseOrderUpdateMutationResponseSchema = yup.object({
  data: yup.object({
    purchase_order_update: yup.object({
      request_id: yup.string().notRequired(),
      complexity: yup.number().notRequired(),
      purchase_order: yup.object({ id: yup.string().required() }).notRequired(),
    }),
  }),
})

/**
 * Write-step input for ShipHero purchase order CANCEL via purchase_order_cancel.
 *
 * po_id must be the ShipHero GraphQL global ID of the purchase order. customer_account_id is
 * required by ShipHero for 3PL accounts acting on behalf of a customer; it is optional here and
 * passed through when present.
 *
 * @see https://developer.shiphero.com/schema/mutations/purchase-order-cancel.html
 */
export const shipHeroCancelPurchaseOrderActionStepInputSchema = yup.object({
  po_id: yup.string().required(),
  customer_account_id: yup.string().notRequired(),
})

export type ShipHeroCancelPurchaseOrderActionStepInput = yup.InferType<
  typeof shipHeroCancelPurchaseOrderActionStepInputSchema
>

export const shipHeroPurchaseOrderCancelMutationResponseSchema = yup.object({
  data: yup.object({
    purchase_order_cancel: yup.object({
      request_id: yup.string().notRequired(),
      complexity: yup.number().notRequired(),
      purchase_order: yup.object({ id: yup.string().required() }).notRequired(),
    }),
  }),
})

// INVENTORY CHANGE -----------------------------------------------------------------------------------------

export const shipHeroInventoryChangeSchema = yup.object({
  webhook_type: yup.string().notRequired().oneOf(['Inventory Change']),
  account_uuid: yup.string().notRequired(),
  account_id: yup.number().integer().notRequired(),
  warehouse_id: yup.number().integer().notRequired(),
  warehouse_uuid: yup.string().notRequired(),
  user_id: yup.number().integer().notRequired(),
  user_uuid: yup.string().notRequired(),
  sku: yup.string().notRequired(),
  quantity: yup.number().integer().notRequired(),
  location_name: yup.string().notRequired(),
  previous_on_hand: yup.number().integer().notRequired(),
  timestamp: yup.string().notRequired(),
  reason: yup.string().notRequired(),
  source: yup.string().notRequired(),
  lot_id: yup.number().integer().notRequired(),
  lot_uuid: yup.string().notRequired(),
  lot_expiration: yup.string().notRequired(),
})

export type ShipHeroInventoryChange = yup.InferType<typeof shipHeroInventoryChangeSchema>

// SHIPMENT UPDATE -----------------------------------------------------------------------------------------

export const shipHeroLineItemSchema = yup.object({
  id: yup.string().notRequired(),
  shiphero_id: yup.number().notRequired(),
  quantity: yup.number().notRequired(),
  sku: yup.string().notRequired(),
  serial_numbers: yup.array().of(yup.string()).notRequired(),
  customs_description: yup.string().notRequired(),
  package: yup.string().notRequired(),
  lot_id: yup.string().notRequired(),
  lot_name: yup.string().notRequired(),
  lot_expiration: yup.string().notRequired(),
})

export const shipHeroPackageSchema = yup.object({
  length: yup.number().notRequired(),
  width: yup.number().notRequired(),
  height: yup.number().notRequired(),
  weight: yup.number().notRequired(),
})

export const shipHeroPackageLineItemSchema = yup.object({
  line_item_id: yup.number().notRequired(),
  partner_line_item_id: yup.string().notRequired(),
  sku: yup.string().notRequired(),
  product_name: yup.string().notRequired(),
  customs_description: yup.string().notRequired(),
  tariff_code: yup.string().notRequired(),
  price: yup.number().notRequired(),
  quantity: yup.number().notRequired(),
  weight: yup.number().notRequired(),
  customs_value: yup.number().notRequired(),
  ignore_on_customs: yup.boolean().notRequired(),
  country_of_manufacture: yup.string().notRequired(),
  lot_id: yup.string().notRequired(),
  lot_name: yup.string().notRequired(),
  lot_expiration: yup.string().notRequired(),
})

export const shipHeroShippingLabelSchema = yup.object({
  shipping_carrier: yup.string().notRequired(),
  shipping_method: yup.string().notRequired(),
  shipping_name: yup.string().notRequired(),
  cost: yup.number().notRequired(),
  tracking_number: yup.string().notRequired(),
  tracking_url: yup.string().notRequired(),
})

export const shipHeroDetailedPackageSchema = yup.object({
  name: yup.string().notRequired(),
  weight_in_oz: yup.number().notRequired(),
  width: yup.number().notRequired(),
  length: yup.number().notRequired(),
  height: yup.number().notRequired(),
  line_items: yup.array().of(shipHeroPackageLineItemSchema).notRequired(),
  shipping_label: shipHeroShippingLabelSchema.notRequired(),
  shipping_address: shipHeroAddressSchema.notRequired(),
  box_code: yup.string().notRequired(),
  box_id: yup.number().notRequired(),
  box_name: yup.string().notRequired(),
})

export const shipHeroFulfillmentSchema = yup.object({
  shipment_id: yup.number().notRequired(),
  shipment_uuid: yup.string().notRequired(),
  warehouse: yup.string().notRequired(),
  warehouse_id: yup.number().notRequired(),
  warehouse_uuid: yup.string().notRequired(),
  account_id: yup.number().notRequired(),
  webhook_type: yup.string().notRequired(),
  partner_order_id: yup.string().notRequired(),
  order_number: yup.string().notRequired(),
  tracking_number: yup.string().notRequired(),
  line_items: yup.array().of(shipHeroLineItemSchema).notRequired(),
  custom_tracking_url: yup.string().notRequired(),
  package_number: yup.number().notRequired(),
  total_packages: yup.number().notRequired(),
  shipping_method: yup.string().notRequired(),
  shipping_carrier: yup.string().notRequired(),
  shipping_address: shipHeroAddressSchema.notRequired(),
  package: shipHeroPackageSchema.notRequired(),
  completed: yup.boolean().notRequired(),
  created_at: yup.string().notRequired(),
  order_uuid: yup.string().notRequired(),
  order_gift_note: yup.string().notRequired(),
})

export const shipHeroShipmentUpdateDataSchema = yup.object({
  test: yup.string().notRequired(),
  webhook_type: yup.string().notRequired(),
  fulfillment: shipHeroFulfillmentSchema.notRequired(),
  total_packages: yup.number().notRequired(),
  packages: yup.array().of(shipHeroDetailedPackageSchema).notRequired(),
})

export type ShipHeroShipmentUpdate = yup.InferType<typeof shipHeroShipmentUpdateDataSchema>

// WEBHOOKS ------------------------------------------------------------------------------------------------

export const shipHeroCreateWebhookResponseSchema = yup.object({
  data: yup.object({
    webhook_create: yup.object({
      request_id: yup.string().notRequired(),
      complexity: yup.number().notRequired(),
      webhook: yup.object({
        shared_signature_secret: yup.string().notRequired(),
      }),
    }),
  }),
})

export type ShipHeroCreateWebhookResponse = yup.InferType<
  typeof shipHeroCreateWebhookResponseSchema
>

// Purchase Order Update Webhook

// Purchase Order Line Item Webhook Schema
export const shipHeroWebhookPurchaseOrderLineItemSchema = yup.object().shape({
  id: yup.string().notRequired(),
  quantity: yup.number().notRequired(),
  quantity_received: yup.number().notRequired(),
  sku: yup.string().notRequired(),
  vendor_id: yup.number().notRequired(),
  vendor_uuid: yup.string().notRequired(),
  vendor_account_number: yup.string().nullable().notRequired(),
  vendor_sku: yup.string().nullable().notRequired(),
})

// Individual Purchase Order Webhook Schema
const shipHeroIndividualPurchaseOrderSchema = yup.object().shape({
  id: yup.number().notRequired(),
  po_number: yup.string().notRequired(),
  po_id: yup.number().notRequired(),
  po_uuid: yup.string().notRequired(),
  account_id: yup.number().notRequired(),
  line_items: yup.array().of(shipHeroWebhookPurchaseOrderLineItemSchema).notRequired(),
  warehouse_id: yup.number().notRequired(),
  status: yup.string().notRequired(),
  webhook_type: yup.string().notRequired(),
})

// Purchase Order Webhook Schema
export const shipHeroPurchaseOrderWebhookSchema = yup.object().shape({
  test: yup.string().notRequired(),
  purchase_order: shipHeroIndividualPurchaseOrderSchema.notRequired(),
})

export type ShipHeroWebhookPurchaseOrder = yup.InferType<typeof shipHeroPurchaseOrderWebhookSchema>

/**
 * Lot/expiration fields hydrated via purchase_order query on PO Update webhooks.
 *
 * @see https://developer.shiphero.com/schema/queries/purchase-order.html
 */
export const shipHeroPurchaseOrderLotSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.number().notRequired(),
  name: yup.string().notRequired(),
  sku: yup.string().notRequired(),
  expires_at: yup.string().notRequired(),
  received_at: yup.string().notRequired(),
  is_active: yup.boolean().notRequired(),
})

export const shipHeroPurchaseOrderLineItemDetailsSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.number().notRequired(),
  sku: yup.string().notRequired(),
  quantity: yup.number().notRequired(),
  quantity_received: yup.number().notRequired(),
  quantity_rejected: yup.number().notRequired(),
  fulfillment_status: yup.string().notRequired(),
  expiration_lots: yup.array().of(shipHeroPurchaseOrderLotSchema).notRequired(),
})

export const shipHeroPurchaseOrderDetailsSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.number().notRequired(),
  po_number: yup.string().notRequired(),
  warehouse_id: yup.string().notRequired(),
  fulfillment_status: yup.string().notRequired(),
  arrived_at: yup.string().notRequired(),
  line_items: yup.array().of(shipHeroPurchaseOrderLineItemDetailsSchema).notRequired(),
})

export type ShipHeroPurchaseOrderDetails = yup.InferType<typeof shipHeroPurchaseOrderDetailsSchema>

/** Raw purchase_order.data from GraphQL before connection flattening. */
const shipHeroPurchaseOrderQueryRawDataSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.number().notRequired(),
  po_number: yup.string().notRequired(),
  warehouse_id: yup.string().notRequired(),
  fulfillment_status: yup.string().notRequired(),
  arrived_at: yup.string().notRequired(),
  line_items: yup.mixed().notRequired(),
})

export const shipHeroPurchaseOrderQueryResponseSchema = yup.object({
  data: yup.object({
    purchase_order: yup.object({
      request_id: yup.string().notRequired(),
      complexity: yup.number().notRequired(),
      data: shipHeroPurchaseOrderQueryRawDataSchema.notRequired(),
    }),
  }),
})

/** PO Update auto-trigger payload: webhook body plus optional hydrated purchase_order query result. */
export const shipHeroPoUpdateWebhookDataSchema = shipHeroPurchaseOrderWebhookSchema.concat(
  yup.object({
    purchase_order_details: shipHeroPurchaseOrderDetailsSchema.notRequired(),
  }),
)

export type ShipHeroPoUpdateWebhookData = yup.InferType<typeof shipHeroPoUpdateWebhookDataSchema>

// Return Update Webhook
// @see https://developer.shiphero.com/webhooks/return-update-webhook/

export const shipHeroReturnUpdateLineItemSchema = yup.object({
  id: yup.number().notRequired(),
  uuid: yup.string().notRequired(),
  quantity: yup.number().notRequired(),
  quantity_received: yup.number().notRequired(),
  sku: yup.string().notRequired(),
  item_reason: yup.string().notRequired(),
  restock: yup.number().notRequired(),
  partner_line_item_id: yup.string().notRequired(),
  warehouse_id: yup.number().notRequired(),
})

export const shipHeroReturnUpdateDataSchema = yup.object({
  test: yup.string().notRequired(),
  webhook_type: yup.string().notRequired(),
  return_id: yup.number().notRequired(),
  return_uuid: yup.string().notRequired(),
  status: yup.string().notRequired(),
  reason: yup.string().notRequired(),
  items: yup.array().of(shipHeroReturnUpdateLineItemSchema).notRequired(),
  order_id: yup.number().notRequired(),
  order_uuid: yup.string().notRequired(),
  partner_order_id: yup.string().notRequired(),
  order_number: yup.string().notRequired(),
  shop_name: yup.string().notRequired(),
  created_at: yup.string().notRequired(),
  label_type: yup.string().notRequired(),
  label_cost: yup.string().notRequired(),
  picked_up: yup.boolean().notRequired(),
  tracking_number: yup.string().notRequired(),
  date_used: yup.string().nullable().notRequired(),
})

export type ShipHeroReturnUpdate = yup.InferType<typeof shipHeroReturnUpdateDataSchema>

// WORK ORDER STATUS UPDATE WEBHOOK
// @see https://developer.shiphero.com/webhooks/work-order-update-webhook/

export const shipHeroWorkOrderStatusUpdateWorkOrderSchema = yup.object({
  id: yup.number().integer().notRequired(),
  work_order_uuid: yup.string().notRequired(),
  work_order_number: yup.number().integer().notRequired(),
  account_id: yup.number().integer().notRequired(),
  warehouse_id: yup.number().integer().notRequired(),
  customer_account_id: yup.number().integer().notRequired(),
  client_id: yup.number().integer().notRequired(),
  name: yup.string().notRequired(),
  type: yup.string().notRequired(),
  previous_status: yup.string().notRequired(),
  new_status: yup.string().notRequired(),
  timestamp: yup.number().notRequired(),
  changed_by: yup.number().integer().notRequired(),
  priority: yup.string().notRequired(),
  created_at: yup.string().notRequired(),
  updated_at: yup.string().notRequired(),
})

export type ShipHeroWorkOrderStatusUpdateWorkOrder = yup.InferType<
  typeof shipHeroWorkOrderStatusUpdateWorkOrderSchema
>

export const shipHeroWorkOrderStatusUpdateDataSchema = yup.object({
  webhook_type: yup.string().notRequired().oneOf(['Work Order Status Update']),
  work_order: shipHeroWorkOrderStatusUpdateWorkOrderSchema.notRequired(),
})

export type ShipHeroWorkOrderStatusUpdate = yup.InferType<
  typeof shipHeroWorkOrderStatusUpdateDataSchema
>

// Order Canceled Webhook
// @see https://developer.shiphero.com/webhooks/order-canceled-webhook/

export const shipHeroOrderCanceledDataSchema = yup.object({
  webhook_type: yup.string().notRequired(),
  order_number: yup.string().notRequired(),
  partner_order_id: yup.string().notRequired(),
  'fulfillment-status': yup.string().notRequired(),
  order_id: yup.number().notRequired(),
  order_uuid: yup.string().notRequired(),
  shop_name: yup.string().notRequired(),
  shop_order_number: yup.string().notRequired(),
})

export type ShipHeroOrderCanceled = yup.InferType<typeof shipHeroOrderCanceledDataSchema>

// Order Allocated Webhook
// @see https://developer.shiphero.com/webhooks/order-allocated-webhook/

export const shipHeroOrderAllocatedLineItemSchema = yup.object({
  id: yup.number().notRequired(),
  item_uuid: yup.string().notRequired(),
  partner_line_item_id: yup.string().notRequired(),
  quantity: yup.number().notRequired(),
  sku: yup.string().notRequired(),
  is_kit_component: yup.boolean().notRequired(),
  created_at: yup.string().notRequired(),
})

export const shipHeroOrderAllocatedDataSchema = yup.object({
  webhook_type: yup.string().notRequired(),
  allocation_reference: yup.number().notRequired(),
  account_id: yup.number().notRequired(),
  account_uuid: yup.string().notRequired(),
  warehouse_id: yup.number().notRequired(),
  warehouse_uuid: yup.string().notRequired(),
  allocated_at: yup.string().notRequired(),
  order_number: yup.string().notRequired(),
  order_id: yup.number().notRequired(),
  order_uuid: yup.string().notRequired(),
  partner_order_id: yup.string().notRequired(),
  line_items: yup.array().of(shipHeroOrderAllocatedLineItemSchema).notRequired(),
  ready_to_ship: yup.number().notRequired(),
})

export type ShipHeroOrderAllocated = yup.InferType<typeof shipHeroOrderAllocatedDataSchema>

// Order Deallocated Webhook
// @see https://developer.shiphero.com/webhooks/order-deallocated-webhook/

export const shipHeroOrderDeallocatedDataSchema = yup.object({
  webhook_type: yup.string().notRequired(),
  allocation_reference: yup.number().notRequired(),
  account_id: yup.number().notRequired(),
  account_uuid: yup.string().notRequired(),
  order_id: yup.number().notRequired(),
  order_number: yup.string().notRequired(),
  order_uuid: yup.string().notRequired(),
  partner_order_id: yup.string().notRequired(),
  deallocated_at: yup.string().notRequired(),
})

export type ShipHeroOrderDeallocated = yup.InferType<typeof shipHeroOrderDeallocatedDataSchema>

// Order Packed Out Webhook
// @see https://developer.shiphero.com/webhooks/order-packed-out-webhook/

export const shipHeroOrderPackedOutItemSchema = yup.object({
  order_id: yup.number().notRequired(),
  order_uuid: yup.string().notRequired(),
  line_item_id: yup.number().notRequired(),
  pick_id: yup.number().notRequired(),
  sku: yup.string().notRequired(),
})

export const shipHeroOrderPackedOutDataSchema = yup.object({
  webhook_type: yup.string().notRequired(),
  tote_id: yup.string().notRequired(),
  tote_uuid: yup.string().notRequired(),
  tote_name: yup.string().notRequired(),
  order_id: yup.number().notRequired(),
  order_uuid: yup.string().notRequired(),
  items: yup.array().of(shipHeroOrderPackedOutItemSchema).notRequired(),
})

export type ShipHeroOrderPackedOut = yup.InferType<typeof shipHeroOrderPackedOutDataSchema>

// INVENTORY UPDATE -----------------------------------------------------------------------------------------

// Individual inventory item schema for inventory updates
export const shipHeroInventoryUpdateItemSchema = yup.object({
  sku: yup.string().notRequired(),
  inventory: yup.number().notRequired(),
  backorder_quantity: yup.number().notRequired(),
  on_hand: yup.number().notRequired(),
  virtual: yup.boolean().notRequired(),
  sell_ahead: yup.number().notRequired(),
  qty_in_totes: yup.number().notRequired(),
  reserve: yup.number().notRequired(),
  updated_warehouse: yup
    .object({
      warehouse_id: yup.number().notRequired(),
      warehouse_uuid: yup.string().notRequired(),
      identifier: yup.string().notRequired(),
      inventory: yup.number().notRequired(),
      backorder_quantity: yup.number().notRequired(),
      on_hand: yup.number().notRequired(),
      sell_ahead: yup.number().notRequired(),
      qty_in_totes: yup.number().notRequired(),
      reserve: yup.number().notRequired(),
      non_sellable: yup.number().notRequired(),
    })
    .notRequired(),
  non_sellable: yup.number().notRequired(),
})

// Inventory Update Webhook Schema
export const shipHeroInventoryUpdateSchema = yup.object({
  account_id: yup.number().notRequired(),
  account_uuid: yup.string().notRequired(),
  webhook_type: yup.string().notRequired(),
  inventory: yup.array().of(shipHeroInventoryUpdateItemSchema).notRequired(),
})

export type ShipHeroInventoryUpdate = yup.InferType<typeof shipHeroInventoryUpdateSchema>

// INVENTORY SNAPSHOT -----------------------------------------------------------------------------------------

/** Fields returned by ShipHero inventory snapshot generate/poll APIs */
export const shipHeroInventorySnapshotSchema = yup.object({
  snapshot_id: yup.string().required(),
  job_user_id: yup.string().notRequired(),
  job_account_id: yup.string().notRequired(),
  warehouse_id: yup.string().notRequired(),
  customer_account_id: yup.string().notRequired().nullable(),
  notification_email: yup.string().notRequired().nullable(),
  email_error: yup.string().notRequired().nullable(),
  post_url: yup.string().notRequired().nullable(),
  post_error: yup.string().notRequired().nullable(),
  post_url_pre_check: yup.boolean().notRequired(),
  status: yup.string().required(),
  error: yup.string().notRequired().nullable(),
  created_at: yup.string().notRequired(),
  enqueued_at: yup.string().notRequired(),
  updated_at: yup.string().notRequired(),
  snapshot_url: yup.string().notRequired().nullable(),
  snapshot_expiration: yup.string().notRequired().nullable(),
  // Populated by us (not ShipHero) after downloading + transforming the JSON at snapshot_url. Absent
  // in raw generate/poll/list GraphQL responses; attached by the read handler / snapshot-post webhook.
  snapshot_data: yup.mixed().notRequired(),
})

export type ShipHeroInventorySnapshot = yup.InferType<typeof shipHeroInventorySnapshotSchema>

export const shipHeroInventoryGenerateSnapshotResponseSchema = yup.object({
  data: yup.object({
    inventory_generate_snapshot: yup.object({
      request_id: yup.string().notRequired(),
      snapshot: shipHeroInventorySnapshotSchema.required(),
    }),
  }),
})

export const shipHeroInventorySnapshotQueryResponseSchema = yup.object({
  data: yup.object({
    inventory_snapshot: yup.object({
      request_id: yup.string().notRequired(),
      snapshot: shipHeroInventorySnapshotSchema.required(),
    }),
  }),
})

export const shipHeroInventoryAbortSnapshotResponseSchema = yup.object({
  data: yup.object({
    inventory_abort_snapshot: yup.object({
      request_id: yup.string().notRequired(),
      snapshot: shipHeroInventorySnapshotSchema.required(),
    }),
  }),
})

export const shipHeroInventorySnapshotsListResponseSchema = yup.object({
  data: yup.object({
    inventory_snapshots: yup.object({
      request_id: yup.string().notRequired(),
      snapshots: yup.object({
        edges: yup
          .array()
          .of(
            yup.object({
              node: shipHeroInventorySnapshotSchema.required(),
            }),
          )
          .notRequired(),
      }),
    }),
  }),
})

export type ShipHeroInventorySnapshotsListResponse = yup.InferType<
  typeof shipHeroInventorySnapshotsListResponseSchema
>

// ITEM LOCATIONS ---------------------------------------------------------------------------------------------

const shipHeroItemLocationLocationTypeSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  account_id: yup.string().notRequired(),
  name: yup.string().notRequired(),
  category: yup.string().notRequired(),
  is_lpn_allowed: yup.boolean().notRequired(),
})

const shipHeroItemLocationLocationSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  account_id: yup.string().notRequired(),
  warehouse_id: yup.string().notRequired(),
  name: yup.string().notRequired(),
  zone: yup.string().notRequired(),
  pickable: yup.boolean().notRequired(),
  sellable: yup.boolean().notRequired(),
  type: shipHeroItemLocationLocationTypeSchema.notRequired(),
})

const shipHeroItemLocationProductSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  account_id: yup.string().notRequired(),
  sku: yup.string().notRequired(),
  name: yup.string().notRequired(),
  barcode: yup.string().notRequired(),
})

const shipHeroItemLocationExpirationLotSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  account_id: yup.string().notRequired(),
  name: yup.string().notRequired(),
  sku: yup.string().notRequired(),
  expires_at: yup.string().notRequired(),
  received_at: yup.string().notRequired(),
  is_active: yup.boolean().notRequired(),
})

/** Warehouse-level inventory totals on an item_locations row (sellable vs non-sellable). */
const shipHeroItemLocationWarehouseProductSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  account_id: yup.string().notRequired(),
  sku: yup.string().notRequired(),
  warehouse_id: yup.string().notRequired(),
  on_hand: yup.number().notRequired(),
  allocated: yup.number().notRequired(),
  available: yup.number().notRequired(),
  non_sellable_quantity: yup.number().notRequired(),
  reserve_inventory: yup.number().notRequired(),
  backorder: yup.number().notRequired(),
})

/** Bin-level inventory row from ShipHero item_locations query */
export const shipHeroItemLocationSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  account_id: yup.string().notRequired(),
  warehouse_id: yup.string().notRequired(),
  location_id: yup.string().notRequired(),
  sku: yup.string().notRequired(),
  quantity: yup.number().notRequired(),
  created_at: yup.string().notRequired(),
  updated_at: yup.string().notRequired(),
  location: shipHeroItemLocationLocationSchema.notRequired(),
  location_type: shipHeroItemLocationLocationTypeSchema.notRequired(),
  product: shipHeroItemLocationProductSchema.notRequired(),
  expiration_lot: shipHeroItemLocationExpirationLotSchema.notRequired(),
  warehouse_product: shipHeroItemLocationWarehouseProductSchema.notRequired(),
})

export type ShipHeroItemLocation = yup.InferType<typeof shipHeroItemLocationSchema>

export const shipHeroItemLocationsQueryResponseSchema = yup.object({
  data: yup.object({
    item_locations: yup.object({
      request_id: yup.string().notRequired(),
      complexity: yup.number().notRequired(),
      data: yup
        .object({
          pageInfo: yup
            .object({
              hasNextPage: yup.boolean().notRequired(),
              hasPreviousPage: yup.boolean().notRequired(),
              endCursor: yup.string().notRequired(),
              startCursor: yup.string().notRequired(),
            })
            .notRequired(),
          edges: yup
            .array()
            .of(
              yup.object({
                cursor: yup.string().notRequired(),
                node: shipHeroItemLocationSchema.required(),
              }),
            )
            .notRequired(),
        })
        .notRequired(),
    }),
  }),
})

export type ShipHeroItemLocationsQueryResponse = yup.InferType<
  typeof shipHeroItemLocationsQueryResponseSchema
>

// RETURNS ----------------------------------------------------------------------------------------------------

/** Lot shipped on the original order line item (for return lot re-mapping). */
export const shipHeroShippedLineItemLotSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  line_item_id: yup.string().notRequired(),
  lot_id: yup.string().notRequired(),
  lot_name: yup.string().notRequired(),
  lot_expiration_date: yup.string().notRequired(),
})

const shipHeroReturnLineItemNestedLineItemSchema = yup.object({
  id: yup.string().notRequired(),
  sku: yup.string().notRequired(),
  partner_line_item_id: yup.string().notRequired(),
  product_name: yup.string().notRequired(),
  shipped_line_item_lots: yup.array().of(shipHeroShippedLineItemLotSchema).notRequired(),
})

/** Return line item from query returns (condition is the item disposition). */
export const shipHeroReturnLineItemSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  quantity: yup.number().notRequired(),
  quantity_received: yup.number().notRequired(),
  restock: yup.number().notRequired(),
  condition: yup.string().notRequired(),
  type: yup.string().notRequired(),
  reason: yup.string().notRequired(),
  warehouse_id: yup.string().notRequired(),
  line_item: shipHeroReturnLineItemNestedLineItemSchema.notRequired(),
})

const shipHeroReturnLabelSchema = yup.object({
  tracking_number: yup.string().notRequired(),
  cost: yup.string().notRequired(),
  carrier: yup.string().notRequired(),
})

const shipHeroReturnOrderSchema = yup.object({
  id: yup.string().notRequired(),
  order_number: yup.string().notRequired(),
  partner_order_id: yup.string().notRequired(),
})

/** Return row from ShipHero query returns */
export const shipHeroReturnSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  account_id: yup.string().notRequired(),
  order_id: yup.string().notRequired(),
  partner_id: yup.string().notRequired(),
  reason: yup.string().notRequired(),
  status: yup.string().notRequired(),
  label_type: yup.string().notRequired(),
  label_cost: yup.string().notRequired(),
  cost_to_customer: yup.string().notRequired(),
  created_at: yup.string().notRequired(),
  line_items: yup.array().of(shipHeroReturnLineItemSchema).notRequired(),
  labels: yup.array().of(shipHeroReturnLabelSchema).notRequired(),
  order: shipHeroReturnOrderSchema.notRequired(),
})

export type ShipHeroReturn = yup.InferType<typeof shipHeroReturnSchema>

export const shipHeroReturnsQueryResponseSchema = yup.object({
  data: yup.object({
    returns: yup.object({
      request_id: yup.string().notRequired(),
      complexity: yup.number().notRequired(),
      data: yup
        .object({
          pageInfo: yup
            .object({
              hasNextPage: yup.boolean().notRequired(),
              endCursor: yup.string().notRequired(),
            })
            .notRequired(),
          edges: yup
            .array()
            .of(
              yup.object({
                cursor: yup.string().notRequired(),
                node: shipHeroReturnSchema.required(),
              }),
            )
            .notRequired(),
        })
        .notRequired(),
    }),
  }),
})

export type ShipHeroReturnsQueryResponse = yup.InferType<typeof shipHeroReturnsQueryResponseSchema>

/** Hydrated return row from ShipHero query return (includes shipped_line_item_lots). */
export const shipHeroReturnDetailsSchema = shipHeroReturnSchema

export type ShipHeroReturnDetails = ShipHeroReturn

export const shipHeroReturnQueryResponseSchema = yup.object({
  data: yup.object({
    return: yup.object({
      request_id: yup.string().notRequired(),
      complexity: yup.number().notRequired(),
      data: shipHeroReturnSchema.notRequired(),
    }),
  }),
})

export type ShipHeroReturnQueryResponse = yup.InferType<typeof shipHeroReturnQueryResponseSchema>

/** Return Update auto-trigger payload: webhook body plus optional hydrated return query result. */
export const shipHeroReturnUpdateWebhookDataSchema = shipHeroReturnUpdateDataSchema.concat(
  yup.object({
    return_details: shipHeroReturnDetailsSchema.notRequired(),
  }),
)

export type ShipHeroReturnUpdateWebhookData = yup.InferType<
  typeof shipHeroReturnUpdateWebhookDataSchema
>

// SHIPMENTS (query shipments) -----------------------------------------------------------------------

/** Address shape used on shipment / shipping-label / warehouse (name rather than first/last). */
export const shipHeroShipmentAddressSchema = yup.object({
  name: yup.string().notRequired(),
  address1: yup.string().notRequired(),
  address2: yup.string().notRequired(),
  city: yup.string().notRequired(),
  state: yup.string().notRequired(),
  country: yup.string().notRequired(),
  zip: yup.string().notRequired(),
  phone: yup.string().notRequired(),
})

export const shipHeroLineItemSerialNumberSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  sku: yup.string().notRequired(),
  line_item_id: yup.string().notRequired(),
  serial_number: yup.string().notRequired(),
  scanned: yup.boolean().notRequired(),
  created_at: yup.string().notRequired(),
  updated_at: yup.string().notRequired(),
})

const shipHeroShipmentNestedLineItemSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  sku: yup.string().notRequired(),
  partner_line_item_id: yup.string().notRequired(),
  product_id: yup.string().notRequired(),
  quantity: yup.number().notRequired(),
  price: yup.mixed().notRequired(),
  product_name: yup.string().notRequired(),
  option_title: yup.string().notRequired(),
  fulfillment_status: yup.string().notRequired(),
  quantity_pending_fulfillment: yup.number().notRequired(),
  quantity_shipped: yup.number().notRequired(),
  warehouse: yup.string().notRequired(),
  quantity_allocated: yup.number().notRequired(),
  backorder_quantity: yup.number().notRequired(),
  custom_options: yup.mixed().notRequired(),
  custom_barcode: yup.string().notRequired(),
  eligible_for_return: yup.boolean().notRequired(),
  customs_value: yup.mixed().notRequired(),
  warehouse_id: yup.string().notRequired(),
  locked_to_warehouse_id: yup.string().notRequired(),
  subtotal: yup.mixed().notRequired(),
  barcode: yup.string().notRequired(),
  created_at: yup.string().notRequired(),
  updated_at: yup.string().notRequired(),
  order_id: yup.string().notRequired(),
  promotion_discount: yup.mixed().notRequired(),
  shipped_line_item_lots: yup.array().of(shipHeroShippedLineItemLotSchema).notRequired(),
  serial_numbers: yup.array().of(shipHeroLineItemSerialNumberSchema).notRequired(),
})

/** Shipment line-item node from query shipments (lots are the packed lots for this shipment). */
export const shipHeroShipmentLineItemSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  line_item_id: yup.string().notRequired(),
  shipment_id: yup.string().notRequired(),
  shipping_label_id: yup.string().notRequired(),
  quantity: yup.number().notRequired(),
  line_item: shipHeroShipmentNestedLineItemSchema.notRequired(),
  lots: yup.array().of(shipHeroShippedLineItemLotSchema).notRequired(),
})

const shipHeroShipmentDimensionsSchema = yup.object({
  weight: yup.mixed().notRequired(),
  height: yup.mixed().notRequired(),
  width: yup.mixed().notRequired(),
  length: yup.mixed().notRequired(),
})

const shipHeroShipmentLabelFilesSchema = yup.object({
  pdf_location: yup.string().notRequired(),
  paper_pdf_location: yup.string().notRequired(),
  thermal_pdf_location: yup.string().notRequired(),
  image_location: yup.string().notRequired(),
})

const shipHeroLastMileLabelSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  shipping_carrier: yup.string().notRequired(),
  shipping_method: yup.string().notRequired(),
  tracking_number: yup.string().notRequired(),
  tracking_url: yup.string().notRequired(),
})

/** Shipping label from query shipments (distinct from the webhook package shipping_label). */
export const shipHeroQueryShippingLabelSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  account_id: yup.string().notRequired(),
  shipment_id: yup.string().notRequired(),
  order_id: yup.string().notRequired(),
  box_id: yup.string().notRequired(),
  box_name: yup.string().notRequired(),
  status: yup.string().notRequired(),
  tracking_number: yup.string().notRequired(),
  alternate_tracking_id: yup.string().notRequired(),
  order_number: yup.string().notRequired(),
  order_account_id: yup.string().notRequired(),
  carrier: yup.string().notRequired(),
  shipping_name: yup.string().notRequired(),
  shipping_method: yup.string().notRequired(),
  cost: yup.mixed().notRequired(),
  box_code: yup.string().notRequired(),
  device_id: yup.string().notRequired(),
  delivered: yup.boolean().notRequired(),
  picked_up: yup.boolean().notRequired(),
  refunded: yup.boolean().notRequired(),
  needs_refund: yup.boolean().notRequired(),
  profile: yup.string().notRequired(),
  partner_fulfillment_id: yup.string().notRequired(),
  full_size_to_print: yup.boolean().notRequired(),
  packing_slip: yup.string().notRequired(),
  warehouse: yup.string().notRequired(),
  warehouse_id: yup.string().notRequired(),
  insurance_amount: yup.mixed().notRequired(),
  carrier_account_id: yup.string().notRequired(),
  source: yup.string().notRequired(),
  created_date: yup.string().notRequired(),
  tracking_url: yup.string().notRequired(),
  package_number: yup.number().notRequired(),
  parcelview_url: yup.string().notRequired(),
  tracking_status: yup.string().notRequired(),
  in_shipping_container: yup.boolean().notRequired(),
  shipping_container_id: yup.string().notRequired(),
  dimensions: shipHeroShipmentDimensionsSchema.notRequired(),
  label: shipHeroShipmentLabelFilesSchema.notRequired(),
  address: shipHeroShipmentAddressSchema.notRequired(),
  last_mile_labels: yup.array().of(shipHeroLastMileLabelSchema).notRequired(),
  serial_numbers: yup.array().of(shipHeroLineItemSerialNumberSchema).notRequired(),
})

const shipHeroShipmentWarehouseSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  account_id: yup.string().notRequired(),
  identifier: yup.string().notRequired(),
  dynamic_slotting: yup.boolean().notRequired(),
  invoice_email: yup.string().notRequired(),
  invoice_note: yup.string().notRequired(),
  invoice_logo_url: yup.string().notRequired(),
  phone_number: yup.string().notRequired(),
  profile: yup.string().notRequired(),
  language: yup.string().notRequired(),
  manual_order_prefix: yup.string().notRequired(),
  show_phone_on_invoice: yup.boolean().notRequired(),
  only_pick_orders_with_required_ship_date_today_or_earlier: yup.boolean().notRequired(),
  exclude_stock_from_available_to_sell: yup.boolean().notRequired(),
  company_name: yup.string().notRequired(),
  company_alias: yup.string().notRequired(),
  active: yup.boolean().notRequired(),
  address: shipHeroShipmentAddressSchema.notRequired(),
  return_address: shipHeroShipmentAddressSchema.notRequired(),
  invoice_address: shipHeroShipmentAddressSchema.notRequired(),
  label_address: shipHeroShipmentAddressSchema.notRequired(),
})

const shipHeroShipmentOrderHoldsSchema = yup.object({
  fraud_hold: yup.boolean().notRequired(),
  address_hold: yup.boolean().notRequired(),
  shipping_method_hold: yup.boolean().notRequired(),
  operator_hold: yup.boolean().notRequired(),
  payment_hold: yup.boolean().notRequired(),
  client_hold: yup.boolean().notRequired(),
})

const shipHeroShipmentOrderSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  order_number: yup.string().notRequired(),
  partner_order_id: yup.string().notRequired(),
  shop_name: yup.string().notRequired(),
  fulfillment_status: yup.string().notRequired(),
  order_date: yup.string().notRequired(),
  total_tax: yup.mixed().notRequired(),
  subtotal: yup.mixed().notRequired(),
  total_discounts: yup.mixed().notRequired(),
  total_price: yup.mixed().notRequired(),
  box_name: yup.string().notRequired(),
  ready_to_ship: yup.boolean().notRequired(),
  auto_print_return_label: yup.boolean().notRequired(),
  custom_invoice_url: yup.string().notRequired(),
  account_id: yup.string().notRequired(),
  updated_at: yup.string().notRequired(),
  created_at: yup.string().notRequired(),
  email: yup.string().notRequired(),
  profile: yup.string().notRequired(),
  gift_note: yup.string().notRequired(),
  packing_note: yup.string().notRequired(),
  required_ship_date: yup.string().notRequired(),
  flagged: yup.boolean().notRequired(),
  saturday_delivery: yup.boolean().notRequired(),
  ignore_address_validation_errors: yup.boolean().notRequired(),
  skip_address_validation: yup.boolean().notRequired(),
  priority_flag: yup.boolean().notRequired(),
  allocation_priority: yup.mixed().notRequired(),
  source: yup.string().notRequired(),
  gift_invoice: yup.boolean().notRequired(),
  allow_partial: yup.boolean().notRequired(),
  require_signature: yup.boolean().notRequired(),
  adult_signature_required: yup.boolean().notRequired(),
  alcohol: yup.boolean().notRequired(),
  expected_weight_in_oz: yup.mixed().notRequired(),
  insurance: yup.boolean().notRequired(),
  insurance_amount: yup.mixed().notRequired(),
  currency: yup.string().notRequired(),
  has_dry_ice: yup.boolean().notRequired(),
  allow_split: yup.boolean().notRequired(),
  hold_until_date: yup.string().notRequired(),
  incoterms: yup.string().notRequired(),
  tax_id: yup.string().notRequired(),
  tax_type: yup.string().notRequired(),
  dry_ice_weight_in_lbs: yup.mixed().notRequired(),
  ftr_exemption: yup.string().notRequired(),
  ftr_exemption_value: yup.string().notRequired(),
  address_is_business: yup.boolean().notRequired(),
  tote_qa: yup.boolean().notRequired(),
  do_not_print_invoice: yup.boolean().notRequired(),
  ignore_payment_capture_errors: yup.boolean().notRequired(),
  service_point_id: yup.string().notRequired(),
  partner_source_name: yup.string().notRequired(),
  tags: yup.mixed().notRequired(),
  shipping_lines: yup.mixed().notRequired(),
  shipping_address: shipHeroAddressSchema.notRequired(),
  billing_address: shipHeroAddressSchema.notRequired(),
  holds: shipHeroShipmentOrderHoldsSchema.notRequired(),
  third_party_shipper: yup
    .object({
      account_number: yup.string().notRequired(),
      zip: yup.string().notRequired(),
      country: yup.string().notRequired(),
    })
    .notRequired(),
})

const shipHeroShipmentLineItemsConnectionSchema = yup.object({
  pageInfo: yup
    .object({
      hasNextPage: yup.boolean().notRequired(),
      endCursor: yup.string().notRequired(),
    })
    .notRequired(),
  edges: yup
    .array()
    .of(
      yup.object({
        cursor: yup.string().notRequired(),
        node: shipHeroShipmentLineItemSchema.required(),
      }),
    )
    .notRequired(),
})

/** Shipment node as returned by ShipHero (line_items is a connection). */
export const shipHeroShipmentApiNodeSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  order_id: yup.string().notRequired(),
  user_id: yup.string().notRequired(),
  warehouse_id: yup.string().notRequired(),
  pending_shipment_id: yup.string().notRequired(),
  address: shipHeroShipmentAddressSchema.notRequired(),
  profile: yup.string().notRequired(),
  picked_up: yup.boolean().notRequired(),
  needs_refund: yup.boolean().notRequired(),
  refunded: yup.boolean().notRequired(),
  delivered: yup.boolean().notRequired(),
  shipped_off_shiphero: yup.boolean().notRequired(),
  dropshipment: yup.boolean().notRequired(),
  completed: yup.boolean().notRequired(),
  created_date: yup.string().notRequired(),
  total_packages: yup.number().notRequired(),
  ready_to_ship: yup.boolean().notRequired(),
  line_items: shipHeroShipmentLineItemsConnectionSchema.notRequired(),
  shipping_labels: yup.array().of(shipHeroQueryShippingLabelSchema).notRequired(),
  warehouse: shipHeroShipmentWarehouseSchema.notRequired(),
  order: shipHeroShipmentOrderSchema.notRequired(),
})

export type ShipHeroShipmentApiNode = yup.InferType<typeof shipHeroShipmentApiNodeSchema>

/** Flattened shipment row for action-step output (line_items is an array of nodes). */
export type ShipHeroShipment = Omit<ShipHeroShipmentApiNode, 'line_items'> & {
  line_items: Array<yup.InferType<typeof shipHeroShipmentLineItemSchema>>
}

export const shipHeroShipmentsQueryResponseSchema = yup.object({
  data: yup.object({
    shipments: yup.object({
      request_id: yup.string().notRequired(),
      complexity: yup.number().notRequired(),
      data: yup
        .object({
          pageInfo: yup
            .object({
              hasNextPage: yup.boolean().notRequired(),
              endCursor: yup.string().notRequired(),
            })
            .notRequired(),
          edges: yup
            .array()
            .of(
              yup.object({
                cursor: yup.string().notRequired(),
                node: shipHeroShipmentApiNodeSchema.required(),
              }),
            )
            .notRequired(),
          total_count: yup.number().notRequired(),
        })
        .notRequired(),
    }),
  }),
})

export type ShipHeroShipmentsQueryResponse = yup.InferType<
  typeof shipHeroShipmentsQueryResponseSchema
>

// WORK ORDERS (query work_order) -----------------------------------------------------------------------

const shipHeroWorkOrderLocationSchema = yup.object({
  id: yup.string().notRequired(),
  name: yup.string().notRequired(),
})

/** Component SKU on an assembly work order. */
export const shipHeroWorkOrderAssemblyComponentSchema = yup.object({
  sku: yup.string().notRequired(),
  account_id: yup.string().notRequired(),
  to_pick_quantity: yup.number().integer().notRequired(),
  picked_quantity: yup.number().integer().notRequired(),
  pick_location_id: yup.string().notRequired(),
})

export type ShipHeroWorkOrderAssemblyComponent = yup.InferType<
  typeof shipHeroWorkOrderAssemblyComponentSchema
>

/** Output (bundle) SKU on an assembly work order. */
export const shipHeroWorkOrderAssemblySkuSchema = yup.object({
  sku: yup.string().notRequired(),
  account_id: yup.string().notRequired(),
  to_create_quantity: yup.number().integer().notRequired(),
  created_quantity: yup.number().integer().notRequired(),
  items_to_pick: yup.number().integer().notRequired(),
  items_per_kit: yup.number().integer().notRequired(),
  lot_name: yup.string().notRequired(),
  lot_expiration_date: yup.string().notRequired(),
  created_at: yup.string().notRequired(),
  updated_at: yup.string().notRequired(),
  components: yup.array().of(shipHeroWorkOrderAssemblyComponentSchema).notRequired(),
  receiving_location: shipHeroWorkOrderLocationSchema.notRequired(),
  staging_location: shipHeroWorkOrderLocationSchema.notRequired(),
})

export type ShipHeroWorkOrderAssemblySku = yup.InferType<typeof shipHeroWorkOrderAssemblySkuSchema>

/** Note on a work order (WorkOrderNotesType). */
export const shipHeroWorkOrderNoteSchema = yup.object({
  note: yup.string().notRequired(),
  type: yup.string().notRequired(),
  account_id: yup.string().notRequired(),
  created_at: yup.string().notRequired(),
  updated_at: yup.string().notRequired(),
})

export type ShipHeroWorkOrderNote = yup.InferType<typeof shipHeroWorkOrderNoteSchema>

/** Work order details from ShipHero query work_order. */
export const shipHeroWorkOrderDetailsSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  account_id: yup.string().notRequired(),
  warehouse_id: yup.string().notRequired(),
  name: yup.string().notRequired(),
  configuration: yup.string().notRequired(),
  type: yup.string().notRequired(),
  status: yup.string().notRequired(),
  priority: yup.string().notRequired(),
  has_special_project: yup.boolean().notRequired(),
  requested_date: yup.string().notRequired(),
  scheduled_date: yup.string().notRequired(),
  started_at: yup.string().notRequired(),
  ended_at: yup.string().notRequired(),
  ready_to_pick_at: yup.string().notRequired(),
  assembly_in_progress_at: yup.string().notRequired(),
  completed_at: yup.string().notRequired(),
  created_at: yup.string().notRequired(),
  updated_at: yup.string().notRequired(),
  assembly_sku: shipHeroWorkOrderAssemblySkuSchema.notRequired(),
  notes: yup.array().of(shipHeroWorkOrderNoteSchema).notRequired(),
})

export type ShipHeroWorkOrderDetails = yup.InferType<typeof shipHeroWorkOrderDetailsSchema>

export const shipHeroWorkOrderQueryResponseSchema = yup.object({
  data: yup.object({
    work_order: yup.object({
      request_id: yup.string().notRequired(),
      complexity: yup.number().notRequired(),
      data: shipHeroWorkOrderDetailsSchema.notRequired(),
    }),
  }),
})

export type ShipHeroWorkOrderQueryResponse = yup.InferType<
  typeof shipHeroWorkOrderQueryResponseSchema
>

export const shipHeroWorkOrdersQueryResponseSchema = yup.object({
  data: yup.object({
    work_orders: yup.object({
      request_id: yup.string().notRequired(),
      complexity: yup.number().notRequired(),
      data: yup
        .object({
          pageInfo: yup
            .object({
              hasNextPage: yup.boolean().notRequired(),
              endCursor: yup.string().notRequired(),
            })
            .notRequired(),
          edges: yup
            .array()
            .of(
              yup.object({
                cursor: yup.string().notRequired(),
                node: yup.mixed().notRequired(),
              }),
            )
            .notRequired(),
        })
        .notRequired(),
    }),
  }),
})

export type ShipHeroWorkOrdersQueryResponse = yup.InferType<
  typeof shipHeroWorkOrdersQueryResponseSchema
>

/** Work Order Status Update auto-trigger payload: webhook body plus hydrated work_order query result. */
export const shipHeroWorkOrderStatusUpdateWebhookDataSchema =
  shipHeroWorkOrderStatusUpdateDataSchema.concat(
    yup.object({
      work_order_details: shipHeroWorkOrderDetailsSchema.notRequired(),
    }),
  )

export type ShipHeroWorkOrderStatusUpdateWebhookData = yup.InferType<
  typeof shipHeroWorkOrderStatusUpdateWebhookDataSchema
>

// INVENTORY CHANGES (query inventory_changes) ---------------------------------------------------------

/** Inventory adjustment row from ShipHero query inventory_changes */
export const shipHeroInventoryChangeRecordSchema = yup.object({
  id: yup.string().notRequired(),
  legacy_id: yup.mixed().notRequired(),
  user_id: yup.mixed().notRequired(),
  account_id: yup.mixed().notRequired(),
  warehouse_id: yup.string().notRequired(),
  sku: yup.string().notRequired(),
  previous_on_hand: yup.number().notRequired(),
  change_in_on_hand: yup.number().notRequired(),
  reason: yup.string().notRequired(),
  cycle_counted: yup.boolean().notRequired(),
  location_id: yup.string().notRequired(),
  lot_id: yup.mixed().notRequired(),
  lot_name: yup.string().notRequired(),
  lot_expiration: yup.string().notRequired(),
  created_at: yup.string().notRequired(),
})

export type ShipHeroInventoryChangeRecord = yup.InferType<
  typeof shipHeroInventoryChangeRecordSchema
>

export const shipHeroInventoryChangesQueryResponseSchema = yup.object({
  data: yup.object({
    inventory_changes: yup.object({
      request_id: yup.string().notRequired(),
      complexity: yup.number().notRequired(),
      data: yup
        .object({
          pageInfo: yup
            .object({
              hasNextPage: yup.boolean().notRequired(),
              endCursor: yup.string().notRequired(),
            })
            .notRequired(),
          edges: yup
            .array()
            .of(
              yup.object({
                cursor: yup.string().notRequired(),
                node: shipHeroInventoryChangeRecordSchema.required(),
              }),
            )
            .notRequired(),
        })
        .notRequired(),
    }),
  }),
})

export type ShipHeroInventoryChangesQueryResponse = yup.InferType<
  typeof shipHeroInventoryChangesQueryResponseSchema
>
